import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
// components
import Label from '../components/label';
import { formatUtcDateTimeAsSaoPaulo } from '../utils/formatTime';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
// sections
import { EnvelopeListHead, EnvelopeListToolbar } from '../sections/@dashboard/envelope';
// context
import { EntityContext } from '../entity';
import { AccountContext } from '../account';
import { looksLikeUuid } from '../utils/looksLikeUuid';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'name', label: 'Nome do Envelope', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'createdAt', label: 'Data de Criação', alignRight: false },
  { id: 'updatedAt', label: 'Última Atualização', alignRight: false },
  { id: '' },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  // Mapear orderBy para os campos corretos do objeto
  let aValue;
  let bValue;
  
  if (orderBy === 'name') {
    aValue = a.envelope_name || '';
    bValue = b.envelope_name || '';
  } else if (orderBy === 'status') {
    aValue = a.status || '';
    bValue = b.status || '';
  } else if (orderBy === 'createdAt') {
    aValue = a.created_at || '';
    bValue = b.created_at || '';
  } else if (orderBy === 'updatedAt') {
    aValue = a.updated_at || '';
    bValue = b.updated_at || '';
  } else {
    aValue = a[orderBy] || '';
    bValue = b[orderBy] || '';
  }
  
  if (bValue < aValue) {
    return -1;
  }
  if (bValue > aValue) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return stabilizedThis
      .map((el) => el[0])
      .filter((envelope) => 
        envelope.envelope_name?.toLowerCase().indexOf(query.toLowerCase()) !== -1
      );
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function MySignaturesPage() {
  const navigate = useNavigate();
  
  const [envelopesList, setEnvelopesList] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [appliedLookup, setAppliedLookup] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [jwtToken, setJwtToken] = useState(null);

  const { getMyEnvelopes, getMyEnvelopeDetails } = useContext(EntityContext);
  const { getSession } = useContext(AccountContext);
  const getMyEnvelopesRef = useRef(getMyEnvelopes);
  
  // Atualizar ref quando getMyEnvelopes mudar
  useEffect(() => {
    getMyEnvelopesRef.current = getMyEnvelopes;
  }, [getMyEnvelopes]);

  // Obter token JWT
  useEffect(() => {
    getSession()
      .then((session) => {
        setJwtToken(session.idToken.jwtToken);
      })
      .catch((error) => {
        console.error('Erro ao obter sessão:', error);
      });
  }, [getSession]);

  // Carregar envelopes
  useEffect(() => {
    if (!jwtToken) return;
    if (appliedLookup) return;
    
    const loadEnvelopes = async () => {
      try {
        setIsLoading(true);
        const data = await getMyEnvelopesRef.current(jwtToken, currentPage, rowsPerPage, statusFilter || null);
        
        if (data) {
          setEnvelopesList(data.envelopes || []);
          setTotalResults(data.total_results || 0);
          setTotalPages(data.total_pages || 1);
          // Sincronizar page com currentPage (API usa base 1, componente usa base 0)
          const apiPage = data.page_number || 1;
          setCurrentPage(apiPage);
          setPage(apiPage - 1); // Converter para base 0 para o componente
        }
      } catch (error) {
        console.error('Erro ao carregar envelopes:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadEnvelopes();
  }, [jwtToken, currentPage, statusFilter, rowsPerPage, appliedLookup]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setCurrentPage(newPage + 1); // API usa página baseada em 1
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setCurrentPage(1);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    const newFilterName = event.target.value;
    setFilterName(newFilterName);
    if (!newFilterName && filterName) {
      setPage(0);
      setCurrentPage(1);
    }
  };

  const handleExactSearch = async () => {
    const trimmed = filterName.trim();
    if (!looksLikeUuid(trimmed) || !jwtToken) {
      return;
    }
    setAppliedLookup(trimmed);
    setPage(0);
    setCurrentPage(1);
    setIsLoading(true);
    try {
      const data = await getMyEnvelopeDetails(jwtToken, trimmed);
      const envelope = data?.envelope;
      setEnvelopesList(envelope ? [envelope] : []);
      setTotalResults(envelope ? 1 : 0);
    } catch (error) {
      setEnvelopesList([]);
      setTotalResults(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearSearch = () => {
    setFilterName('');
    setAppliedLookup('');
    setPage(0);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
    setCurrentPage(1);
    // O useEffect vai recarregar automaticamente quando statusFilter mudar
  };

  const handleViewDetails = (envelopeKey) => {
    navigate(`/dashboard/my-signatures/${envelopeKey}`);
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'running': { text: 'Em Andamento', color: 'warning' },
      'finished': { text: 'Assinado', color: 'success' },
      'cancelled': { text: 'Cancelado', color: 'error' },
    };
    return statusMap[status] || { text: status, color: 'default' };
  };

  // Aplicar ordenação e filtro no frontend
  const filteredEnvelopes = applySortFilter(envelopesList, getComparator(order, orderBy), appliedLookup ? '' : filterName);

  const isNotFound = !isLoading && filteredEnvelopes.length === 0 && !!(appliedLookup || filterName);

  return (
    <>
      <Helmet>
        <title> Minhas Assinaturas </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Minhas Assinaturas
          </Typography>
        </Stack>

        <Card>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ p: 2 }}>
            <EnvelopeListToolbar 
              numSelected={0} 
              filterName={filterName} 
              onFilterName={handleFilterByName}
              onExactSearch={handleExactSearch}
              onClearSearch={handleClearSearch}
              hasExactSearch={Boolean(appliedLookup)}
              searchPlaceholder="UUID do envelope"
            />
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Filtrar por Status</InputLabel>
              <Select
                value={statusFilter}
                label="Filtrar por Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="running">Em Andamento</MenuItem>
                <MenuItem value="finished">Assinado</MenuItem>
                <MenuItem value="cancelled">Cancelado</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <EnvelopeListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={envelopesList.length}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell align="center" colSpan={5} sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          Carregando...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredEnvelopes.length === 0 ? (
                    <TableRow>
                      <TableCell align="center" colSpan={5} sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          Nenhum envelope encontrado
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredEnvelopes.map((row) => {
                      const statusInfo = getStatusLabel(row.status);

                      return (
                        <TableRow hover key={row.envelope_key} tabIndex={-1}>
                          <TableCell component="th" scope="row" padding="normal">
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Typography variant="subtitle2" noWrap>
                                {row.envelope_name}
                              </Typography>
                            </Stack>
                          </TableCell>

                          <TableCell align="left">
                            <Label color={statusInfo.color}>{statusInfo.text}</Label>
                          </TableCell>

                          <TableCell align="left">
                            {row.created_at ? formatUtcDateTimeAsSaoPaulo(row.created_at) || '-' : '-'}
                          </TableCell>

                          <TableCell align="left">
                            {row.updated_at ? formatUtcDateTimeAsSaoPaulo(row.updated_at) || '-' : '-'}
                          </TableCell>

                          <TableCell align="right">
                            <IconButton size="large" color="primary" onClick={() => handleViewDetails(row.envelope_key)}>
                              <Iconify icon={'eva:eye-fill'} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={5} sx={{ py: 3 }}>
                        <Paper
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="h6" paragraph>
                            Não encontrado
                          </Typography>

                          <Typography variant="body2">
                            Nenhum resultado encontrado para &nbsp;
                            <strong>&quot;{filterName}&quot;</strong>.
                            <br /> Tente verificar erros de digitação ou usar palavras completas.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={appliedLookup ? filteredEnvelopes.length : totalResults}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </>
  );
}
